import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { AddComplianceChecklistRequirementsRequest, ComplianceChecklist, CustomComplianceRequirement } from '../../../models/compliances.model';
import { CHECKLIST_TYPE_NAME_KEYS, CHECKLIST_TYPES, ChecklistTypeIdEnum } from '../../../../_shared/models/checklist-type.model';
import { CompliancesLibraryService } from '../../../services/compliances-library.service';
import { ComplianceOverviewService } from '../../../../compliance-overview/services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../../../_shared/services/modals/confirmation-modal.service';
import { LinkDocumentTypeModalComponent } from '../../../../_shared/components/modals/link-document-type-modal/link-document-type-modal.component';
import { LinkDocumentTypeResult, LinkedDocumentTypeUsage } from '../../../../_shared/models/link-document-type.model';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-add-compliance-requirements-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDropdownModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './add-compliance-requirements-modal.component.html',
  styleUrl: './add-compliance-requirements-modal.component.scss'
})
export class AddComplianceRequirementsModalComponent implements OnInit, OnDestroy {
  @Input() complianceId: number = 0;

  private activeModal = inject(NgbActiveModal);
  private modalService = inject(NgbModal);
  private confirmationModalService = inject(ConfirmationModalService);
  private complianceService = inject(CompliancesLibraryService);
  private complianceOverviewService = inject(ComplianceOverviewService);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();

  readonly checklistTypes = CHECKLIST_TYPES;

  checklistTypeId: number | null = null;
  newCategoryName = '';
  categoryError = '';
  requirements: CustomComplianceRequirement[] = [];

  existingChecklists: ComplianceChecklist[] = [];
  private existingLinks: LinkedDocumentTypeUsage[] = [];
  isSaving = false;

  ngOnInit(): void {
    void this.loadCategoriesAsync();
    void this.loadExistingLinksAsync();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  get checklistTypeLabel(): string {
    const selected = this.checklistTypes.find(x => x.id === this.checklistTypeId);
    return selected
      ? this.checklistTypeName(selected.id)
      : this.transloco.translate('compliance.library.addRequirementsModal.selectChecklistType');
  }

  checklistTypeName(id: ChecklistTypeIdEnum): string {
    return this.transloco.translate(CHECKLIST_TYPE_NAME_KEYS[id]);
  }

  /** Inspection requirements cannot link document types, so the link button is hidden. */
  get isInspectionChecklistSelected(): boolean {
    return this.checklistTypeId === ChecklistTypeIdEnum.Inspection;
  }

  get isSaveDisabled(): boolean {
    return this.isSaving
      || this.checklistTypeId === null
      || !this.newCategoryName.trim()
      || !!this.categoryError
      || this.namedRequirements.length === 0;
  }

  private get namedRequirements(): CustomComplianceRequirement[] {
    return this.requirements.filter(requirement => requirement.name.trim());
  }

  selectChecklistType(id: number): void {
    this.checklistTypeId = id;
    if (this.isInspectionChecklistSelected) {
      this.requirements.forEach(requirement => {
        requirement.documentTypeId = null;
        requirement.documentTypeName = null;
      });
    }
    this.validateCategoryName();
  }

  validateCategoryName(): void {
    if (!this.newCategoryName.trim()) {
      this.categoryError = '';
      return;
    }
    const lower = this.normalizeName(this.newCategoryName);
    const exists = this.existingChecklists.some(c =>
      c.checklistTypeId === this.checklistTypeId && this.normalizeName(c.name) === lower);
    this.categoryError = exists
      ? this.transloco.translate('compliance.library.addRequirementsModal.categoryExists', { name: this.newCategoryName.trim() })
      : '';
  }

  addRequirement(): void {
    this.requirements.push({ name: '', documentTypeId: null, documentTypeName: null, critical: false });
  }

  removeRequirement(index: number): void {
    this.requirements.splice(index, 1);
  }

  openLinkDocumentTypeModal(row: CustomComplianceRequirement): void {
    const modalRef = this.modalService.open(LinkDocumentTypeModalComponent, {
      centered: true,
      windowClass: 'link-document-type-modal-window'
    });
    modalRef.componentInstance.requirementName = row.name;
    modalRef.componentInstance.usedTypes = this.collectUsedTypes(row);

    modalRef.result.then(
      (result: LinkDocumentTypeResult) => {
        row.documentTypeId = result.documentTypeId;
        row.documentTypeName = result.documentTypeName;
      },
      () => {
        // Dismissed.
      }
    );
  }

  confirmUnlinkDocumentType(row: CustomComplianceRequirement): void {
    void this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.addRequirementsModal.confirmations.unlink.title'),
      message: this.transloco.translate('compliance.library.addRequirementsModal.confirmations.unlink.message'),
      warningMessage: this.transloco.translate('compliance.library.addRequirementsModal.confirmations.unlink.warning'),
      confirmText: this.transloco.translate('compliance.library.addRequirementsModal.confirmations.unlink.confirm'),
      declineText: this.transloco.translate('compliance.library.addRequirementsModal.confirmations.unlink.decline'),
      onConfirm: async () => {
        row.documentTypeId = null;
        row.documentTypeName = null;
      }
    });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async saveAsync(): Promise<void> {
    if (this.isSaveDisabled) return;
    this.isSaving = true;
    try {
      const body: AddComplianceChecklistRequirementsRequest = {
        complianceId: this.complianceId,
        isNewCategory: true,
        checklistTypeId: this.checklistTypeId!,
        existingChecklistId: null,
        newCategoryName: this.newCategoryName.trim(),
        requirements: this.namedRequirements.map(requirement => ({
          name: requirement.name.trim(),
          documentTypeId: requirement.documentTypeId,
          isCritical: requirement.critical ?? false
        })),
        allowDocumentUpload: true
      };
      await this.complianceService.addComplianceChecklistRequirementsAsync(body);
      this.toastr.success(this.transloco.translate('compliance.library.addRequirementsModal.toasts.added'));
      this.activeModal.close(true);
    } catch (error) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  private collectUsedTypes(current: CustomComplianceRequirement): LinkedDocumentTypeUsage[] {
    const draft = this.requirements
      .filter(requirement => requirement !== current && requirement.documentTypeId !== null && requirement.documentTypeId !== undefined)
      .map(requirement => ({
        documentTypeId: requirement.documentTypeId!,
        requirementName: requirement.name.trim() || this.transloco.translate('compliance.library.addRequirementsModal.anotherRequirement')
      }));

    return [...this.existingLinks, ...draft];
  }

  private normalizeName(value: string): string {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private async loadCategoriesAsync(): Promise<void> {
    try {
      const response = await this.complianceService.getChecklistsAsync(
        { complianceId: this.complianceId, isArchived: false },
        this.cancellation
      );
      this.existingChecklists = response.data ?? [];
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    }
  }

  private async loadExistingLinksAsync(): Promise<void> {
    if (!this.complianceId) {
      return;
    }

    try {
      const response = await this.complianceOverviewService.getRequirementDocumentLinksAsync(this.complianceId);
      this.existingLinks = (response.data ?? []).map(link => ({
        documentTypeId: link.documentTypeId,
        requirementName: link.requirementName
      }));
    } catch {
      this.existingLinks = [];
    }
  }
}
